import { useLayoutEffect, useRef } from 'react';

const EVT = 'global-filters-ready';
const events = new EventTarget();

/**
 * A "sticky" memory of which filters have already signaled readiness.
 *
 * Why this exists:
 * - Events are transient (fire-and-forget)
 * - If a filter becomes ready BEFORE a listener subscribes,
 *   that information would be lost forever
 *
 * This Set stores readiness so late subscribers can "replay"
 * everything that already happened.
 */
export type SingleFilterReadyPayload = { filterId: string; isReady: boolean };

const readyFilterIds = new Set<SingleFilterReadyPayload>();

/**
 * Called by an individual filter when it finishes initializing.
 *
 * NOTE:
 * This function may be called BEFORE any listener
 * (e.g. GlobalFiltersInPage) has subscribed.
 *
 * For that reason, readiness is:
 * 1) Stored in `readyFilterIds` (persistent memory)
 * 2) Emitted as an event (for current subscribers)
 */
export const signalSingleFilterIsReady = (singleFilterData: SingleFilterReadyPayload) => {
    readyFilterIds.add(singleFilterData);
    events.dispatchEvent(new CustomEvent<SingleFilterReadyPayload>(EVT, { detail: singleFilterData }));
};

/**
 * Subscribes to single-filter readiness notifications.
 *
 * This hook handles TWO distinct readiness cases:
 *
 * 1) Filters that became ready BEFORE this hook subscribed:
 *    - Their readiness is stored in `readyFilterIds`
 *    - On subscription, those entries are replayed immediately
 *
 * 2) Filters that become ready AFTER this hook subscribed:
 *    - Their readiness is delivered via the live event subscription
 *
 * Using both mechanisms ensures that filter readiness
 * is handled correctly regardless of component mount order,
 * route changes, or effect execution timing.
 */

export function useOnSingleFilterReady(callback: (payload: SingleFilterReadyPayload) => void) {
    const cbRef = useRef(callback);
    cbRef.current = callback;

    useLayoutEffect(() => {
        // replay anything that happened before subscription
        readyFilterIds.forEach((singleFilterData) => cbRef.current(singleFilterData));

        const handler = (e: Event) => cbRef.current((e as CustomEvent<SingleFilterReadyPayload>).detail);

        events.addEventListener(EVT, handler);
        return () => {
            events.removeEventListener(EVT, handler);
        };
    }, []);
}
